import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, CalendarDays, Ban } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtBRL } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY_REGION = { name: "", fee: "", neighborhoods: [], cep_ranges: [], exceptions: [], active: true };
const WEEKDAY_LABELS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

export default function AdminDelivery() {
  const [regions, setRegions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [orders, setOrders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_REGION);
  const [nbText, setNbText] = useState("");
  const [exText, setExText] = useState("");
  const [ranges, setRanges] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [blockedInput, setBlockedInput] = useState("");

  const load = () => {
    api.get("/admin/delivery-regions").then((r) => setRegions(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!settings) return <div className="animate-pulse h-64 bg-white rounded-xl border border-slate-200" />;

  const shipping = settings.shipping || {};
  const schedule = settings.schedule || {};

  const openCreate = () => {
    setEditing(null); setForm(EMPTY_REGION); setNbText(""); setExText(""); setRanges([]); setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, fee: r.fee ?? "", active: r.active });
    setNbText((r.neighborhoods || []).join(", "));
    setExText((r.exceptions || []).join(", "));
    setRanges(r.cep_ranges || []);
    setDialogOpen(true);
  };

  const saveRegion = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Informe o nome da cidade/região.");
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      fee: form.fee === "" || form.fee === null ? null : Number(form.fee),
      neighborhoods: nbText.split(",").map((s) => s.trim()).filter(Boolean),
      exceptions: exText.split(",").map((s) => s.trim()).filter(Boolean),
      cep_ranges: ranges.filter((r) => r.from && r.to),
      active: form.active,
    };
    try {
      if (editing) {
        await api.put(`/admin/delivery-regions/${editing.id}`, payload);
        toast.success("Região atualizada!");
      } else {
        await api.post("/admin/delivery-regions", payload);
        toast.success("Região criada!");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/delivery-regions/${deleting.id}`);
      toast.success("Região excluída.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveShipping = async () => {
    try {
      await api.put("/admin/settings", { shipping });
      toast.success("Taxas de entrega salvas!");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveSchedule = async () => {
    try {
      await api.put("/admin/settings", { schedule });
      toast.success("Agendamento salvo!");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setShip = (k, v) => setSettings((s) => ({ ...s, shipping: { ...s.shipping, [k]: v } }));
  const setSched = (k, v) => setSettings((s) => ({ ...s, schedule: { ...s.schedule, [k]: v } }));

  const todayIso = new Date().toISOString().slice(0, 10);
  const upcoming = orders
    .filter((o) => o.delivery_date >= todayIso && !["cancelado", "concluido"].includes(o.status))
    .sort((a, b) => a.delivery_date.localeCompare(b.delivery_date));

  return (
    <div data-testid="admin-delivery" className="space-y-8 max-w-5xl">
      <div>
        <h1 className="font-heading text-2xl font-bold text-gray-900">Entregas</h1>
        <p className="text-sm text-gray-500 mt-1">Regiões atendidas, taxas, agendamento e capacidade diária</p>
      </div>

      {/* taxa global */}
      <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h2 className="font-heading font-semibold text-gray-900">Taxa de entrega</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label>Taxa padrão (R$)</Label>
            <Input type="number" step="0.01" min="0" value={shipping.default_fee ?? ""}
              onChange={(e) => setShip("default_fee", Number(e.target.value))} className="mt-1.5" data-testid="shipping-default-fee" />
          </div>
          <div>
            <Label>Taxa promocional (R$)</Label>
            <Input type="number" step="0.01" min="0" value={shipping.promo_fee ?? ""}
              onChange={(e) => setShip("promo_fee", Number(e.target.value))} className="mt-1.5" data-testid="shipping-promo-fee" />
          </div>
          <div>
            <Label>Nome da promoção</Label>
            <Input value={shipping.promo_label || ""} onChange={(e) => setShip("promo_label", e.target.value)}
              className="mt-1.5" data-testid="shipping-promo-label" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <Switch checked={!!shipping.promo_active} onCheckedChange={(v) => setShip("promo_active", v)} data-testid="shipping-promo-active" />
          Promoção de entrega ativa (vale a taxa promocional; regiões podem ter taxa própria)
        </label>
        <Button onClick={saveShipping} className="rounded-full" data-testid="shipping-save">
          <Save className="w-4 h-4" /> Salvar taxas
        </Button>
      </section>

      {/* regiões */}
      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-heading font-semibold text-gray-900">Regiões atendidas</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Uma cidade só é atendida por completo se a lista de bairros estiver vazia — preenchida, só os bairros listados são atendidos.
            </p>
          </div>
          <Button onClick={openCreate} className="rounded-full" data-testid="region-create-button">
            <Plus className="w-4 h-4" /> Nova Região
          </Button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="p-3">Região</th>
                <th className="p-3">Cobertura</th>
                <th className="p-3">Taxa</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {regions.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-slate-400">Nenhuma região cadastrada.</td></tr>
              ) : (
                regions.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`admin-region-row-${r.id}`}>
                    <td className="p-3 font-medium text-gray-800">{r.name}</td>
                    <td className="p-3 text-slate-600 text-xs">
                      {(r.neighborhoods || []).length > 0
                        ? `${r.neighborhoods.length} bairro(s)`
                        : "Cidade toda"}
                      {(r.cep_ranges || []).length > 0 && ` + ${r.cep_ranges.length} faixa(s) de CEP`}
                      {(r.exceptions || []).length > 0 && ` • ${r.exceptions.length} exceção(ões)`}
                    </td>
                    <td className="p-3">
                      {r.fee != null ? (
                        <span className="font-semibold text-gray-900">{r.fee > 0 ? fmtBRL(r.fee) : "Grátis"}</span>
                      ) : (
                        <span className="text-slate-400">Taxa global</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${r.active ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                        {r.active ? "Ativa" : "Inativa"}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`region-edit-${r.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => setDeleting(r)} data-testid={`region-delete-${r.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* agendamento */}
      <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h2 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-brand" /> Agendamento de entregas
        </h2>
        <div>
          <Label>Dias de entrega permitidos</Label>
          <div className="mt-2 flex flex-wrap gap-4">
            {WEEKDAY_LABELS.map((label, idx) => (
              <label key={label} className="flex items-center gap-2 text-sm text-gray-700">
                <Checkbox
                  checked={(schedule.allowed_weekdays || []).includes(idx)}
                  onCheckedChange={(v) => {
                    const cur = new Set(schedule.allowed_weekdays || []);
                    if (v) cur.add(idx); else cur.delete(idx);
                    setSched("allowed_weekdays", [...cur].sort());
                  }}
                  data-testid={`weekday-${idx}`}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Antecedência mínima (dias)</Label>
            <Input type="number" min="0" value={schedule.min_days ?? 1}
              onChange={(e) => setSched("min_days", Number(e.target.value))} className="mt-1.5" data-testid="schedule-min-days" />
          </div>
          <div>
            <Label>Capacidade de entregas por dia</Label>
            <Input type="number" min="1" value={schedule.capacity_per_day ?? 10}
              onChange={(e) => setSched("capacity_per_day", Number(e.target.value))} className="mt-1.5" data-testid="schedule-capacity" />
          </div>
        </div>
        <div>
          <Label className="flex items-center gap-1.5"><Ban className="w-3.5 h-3.5 text-red-500" /> Datas bloqueadas</Label>
          <div className="mt-2 flex gap-2">
            <Input type="date" value={blockedInput} onChange={(e) => setBlockedInput(e.target.value)} className="sm:w-56" data-testid="blocked-date-input" />
            <Button type="button" variant="outline" onClick={() => {
              if (blockedInput && !(schedule.blocked_dates || []).includes(blockedInput)) {
                setSched("blocked_dates", [...(schedule.blocked_dates || []), blockedInput].sort());
                setBlockedInput("");
              }
            }} data-testid="blocked-date-add">
              Bloquear data
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {(schedule.blocked_dates || []).map((d) => (
              <span key={d} className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 text-xs font-medium rounded-full px-3 py-1">
                {d.split("-").reverse().join("/")}
                <button type="button" onClick={() => setSched("blocked_dates", schedule.blocked_dates.filter((x) => x !== d))}
                  className="hover:text-red-900" data-testid={`blocked-date-remove-${d}`}>×</button>
              </span>
            ))}
            {(schedule.blocked_dates || []).length === 0 && (
              <span className="text-xs text-slate-400">Nenhuma data bloqueada.</span>
            )}
          </div>
        </div>
        <Button onClick={saveSchedule} className="rounded-full" data-testid="schedule-save">
          <Save className="w-4 h-4" /> Salvar agendamento
        </Button>
      </section>

      {/* entregas agendadas */}
      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="font-heading font-semibold text-gray-900">Próximas entregas</h2>
        {upcoming.length === 0 ? (
          <p className="mt-3 text-sm text-slate-400">Nenhuma entrega agendada.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100">
            {upcoming.map((o) => (
              <li key={o.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                <div>
                  <p className="font-medium text-gray-800">{o.order_number || `#${o.id}`} — {o.customer.name}</p>
                  <p className="text-xs text-slate-400">
                    {o.customer.city} — {o.customer.neighborhood}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-semibold text-brand">{o.delivery_date.split("-").reverse().join("/")}</p>
                  <p className="text-xs text-slate-400">{o.status.replace(/_/g, " ")}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* dialog região */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="region-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Região" : "Nova Região"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveRegion} className="space-y-4 mt-2">
            <div>
              <Label>Cidade/região *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Nova Iguaçu" className="mt-1.5" required data-testid="region-form-name" />
            </div>
            <div>
              <Label>Taxa própria (R$) — vazio = usa a taxa global</Label>
              <Input type="number" step="0.01" min="0" value={form.fee}
                onChange={(e) => setForm({ ...form, fee: e.target.value })} className="mt-1.5" data-testid="region-form-fee" />
            </div>
            <div>
              <Label>Bairros atendidos (vírgula) — vazio = cidade toda</Label>
              <Input value={nbText} onChange={(e) => setNbText(e.target.value)}
                placeholder="Centro, Austin, Vilar dos Teles" className="mt-1.5" data-testid="region-form-neighborhoods" />
            </div>
            <div>
              <Label>Bairros NÃO atendidos — exceções (vírgula)</Label>
              <Input value={exText} onChange={(e) => setExText(e.target.value)}
                placeholder="Ex: Bairro distante" className="mt-1.5" data-testid="region-form-exceptions" />
            </div>
            <div>
              <Label>Faixas de CEP atendidas</Label>
              {ranges.map((r, i) => (
                <div key={i} className="mt-2 flex items-center gap-2">
                  <Input value={r.from} placeholder="De (ex: 26000000)" onChange={(e) => {
                    const c = [...ranges]; c[i] = { ...c[i], from: e.target.value }; setRanges(c);
                  }} data-testid={`cep-range-from-${i}`} />
                  <Input value={r.to} placeholder="Até (ex: 26099999)" onChange={(e) => {
                    const c = [...ranges]; c[i] = { ...c[i], to: e.target.value }; setRanges(c);
                  }} data-testid={`cep-range-to-${i}`} />
                  <Button type="button" variant="ghost" size="icon" className="text-red-500"
                    onClick={() => setRanges(ranges.filter((_, idx) => idx !== i))}>×</Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" className="mt-2"
                onClick={() => setRanges([...ranges, { from: "", to: "" }])} data-testid="cep-range-add">
                + Adicionar faixa de CEP
              </Button>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="region-form-active" />
              Ativa no checkout
            </label>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="region-form-save">
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir região?</AlertDialogTitle>
            <AlertDialogDescription>"{deleting?.name}" deixará de receber pedidos pelo site.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="region-delete-confirm">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
